import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

export type IframeAutoResizeMessage = {
  type: "ZU_IFRAME_RESIZE";
  height: number;
  pathname?: string;
};

function getTargetOrigin(): string {
  // Safer default than "*": if we have a referrer, use it.
  // If not, fall back to "*" (useful for local dev / unknown parents).
  try {
    const referrer = document.referrer;
    if (referrer) return new URL(referrer).origin;
  } catch {
    // ignore
  }
  return "*";
}

export function useIframeAutoResize(options?: {
  /** Minimum iframe height, in px */
  minHeight?: number;
  /** Extra pixels to add (e.g. to account for parent padding/borders) */
  heightPadding?: number;
  /** Override postMessage targetOrigin. Prefer a specific origin for security. */
  targetOrigin?: string;
  /** Enable/disable (default true) */
  enabled?: boolean;
}) {
  const location = useLocation();
  const targetOrigin = useMemo(
    () => options?.targetOrigin ?? getTargetOrigin(),
    [options?.targetOrigin],
  );

  const rafIdRef = useRef<number | null>(null);
  const lastHeightRef = useRef<number>(-1);

  useEffect(() => {
    if (options?.enabled === false) return;
    if (typeof window === "undefined") return;
    if (window.parent === window) return;

    const root =
      (document.getElementById("root") as HTMLElement | null) ??
      document.body ??
      document.documentElement;

    const minHeight = options?.minHeight ?? 0;
    const heightPadding = options?.heightPadding ?? 0;

    const postHeight = (height: number) => {
      const payload: IframeAutoResizeMessage = {
        type: "ZU_IFRAME_RESIZE",
        height,
        pathname: location.pathname,
      };
      window.parent.postMessage(payload, targetOrigin);
    };

    const measureAndPost = () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);

      rafIdRef.current = requestAnimationFrame(() => {
        let contentHeight: number;

        if (
          root &&
          root !== document.body &&
          root !== document.documentElement
        ) {
          contentHeight = Math.max(
            root.scrollHeight,
            root.offsetHeight,
            root.clientHeight,
          );
        } else {
          const body = document.body;
          const html = document.documentElement;
          contentHeight = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight,
          );
        }

        const nextHeight = Math.max(
          minHeight,
          Math.ceil(contentHeight + heightPadding),
        );

        // Only update if height changed by more than 2 pixels (threshold)
        if (Math.abs(nextHeight - lastHeightRef.current) > 2) {
          lastHeightRef.current = nextHeight;
          postHeight(nextHeight);
        }
      });
    };

    measureAndPost();

    const resizeObserver = new ResizeObserver(() => {
      measureAndPost();
    });
    resizeObserver.observe(root);

    // Also catch viewport changes (mobile address bar, orientation, etc)
    window.addEventListener("resize", measureAndPost, { passive: true });

    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureAndPost);
    };
    // Intentionally re-run on route changes so parent resizes on navigation.
  }, [
    location.pathname,
    options?.enabled,
    options?.heightPadding,
    options?.minHeight,
    targetOrigin,
  ]);
}
